import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQueryClient,
    InfiniteData,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import { parseMutationResponse, MutationResponse } from '../shared.types';
import {
    contactSearchResponseSchema,
    ContactSearchRequest,
    ContactSearchResponse,
} from './locationContact.types';

export type { Contact, ContactSearchRequest, ContactSearchResponse } from './locationContact.types';

// ---- Query key ----

export const LOCATION_CONTACTS_QUERY_KEY = 'LocationContacts';

const PAGE_SIZE = 20;

// ---- Search location contacts (paginated) ----

const searchLocationContacts = async (
    request: ContactSearchRequest
): Promise<ContactSearchResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-contact/search`, { json: request })
        .json();
    const parsed = contactSearchResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('searchLocationContacts: response did not match schema', parsed.error);
        return { results: [], totalCount: 0, isError: false, errors: [] };
    }
    return parsed.data;
};

export const useSearchLocationContacts = (
    locationKey: string | null | undefined,
    sortBy = 'firstName'
): UseInfiniteQueryResult<InfiniteData<ContactSearchResponse>> =>
    useInfiniteQuery<ContactSearchResponse>({
        queryKey: [LOCATION_CONTACTS_QUERY_KEY, locationKey, sortBy],
        queryFn: ({ pageParam }) =>
            searchLocationContacts({
                pageSize: PAGE_SIZE,
                pageNumber: (pageParam as number) ?? 1,
                locationKey: locationKey!,
                sortBy,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            // Drive hasNextPage off totalCount to avoid looping forever when
            // the client-side active filter removes items.
            const fetched = allPages.flatMap(p => p.results).length;
            return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
        },
        enabled: !!locationKey,
    });

// ---- Create location contact ----

const createLocationContact = async ({
    locationKey,
    userId,
}: {
    locationKey: string;
    userId: number;
}): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-contact/create`, {
            json: { locationKey, userId },
        })
        .json();
    return parseMutationResponse(response);
};

export const useCreateLocationContact = (
    locationKey?: string
): UseMutationResult<MutationResponse, Error, { locationKey: string; userId: number }> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, { locationKey: string; userId: number }>({
        mutationFn: createLocationContact,
        onSuccess: () => {
            if (locationKey) {
                queryClient.invalidateQueries({
                    queryKey: [LOCATION_CONTACTS_QUERY_KEY, locationKey],
                });
            }
        },
    });
};

// ---- Remove location contact ----

const removeLocationContact = async ({
    locationKey,
    userId,
}: {
    locationKey: string;
    userId: number;
}): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-contact/remove`, {
            json: { locationKey, userId },
        })
        .json();
    return parseMutationResponse(response);
};

export const useRemoveLocationContact = (
    locationKey?: string
): UseMutationResult<MutationResponse, Error, { locationKey: string; userId: number }> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, { locationKey: string; userId: number }>({
        mutationFn: removeLocationContact,
        onSuccess: () => {
            if (locationKey) {
                queryClient.invalidateQueries({
                    queryKey: [LOCATION_CONTACTS_QUERY_KEY, locationKey],
                });
            }
        },
    });
};
